import { useEffect, useId, useState, type JSX } from 'react';
import { ExplicacaoTabelas } from './ExplicacaoTabelas.js';
import { mensagemDoErro } from '../lib/api.js';
import { ROTULO_PAGAMENTO, rotuloTabela } from '../lib/formato.js';
import {
  TABELA_DEL_CREDERE,
  buscarNiveisDelCredere,
  type NivelDelCredere,
} from '../estado/modo.js';
import type { ItemSemPreco, Orcamento } from '../estado/gerador.js';
import './DuplicarComo.css';

/**
 * "Duplicar como…" — escolher a condição do novo rascunho.
 *
 * ## O caso de uso
 *
 * "Fiz em CPF à vista, quero ver como fica em RT10 a prazo" — sem refazer os
 * itens. O orçamento original não é tocado: a duplicação sempre cria um
 * rascunho novo.
 *
 * ## Os dois campos vêm PREENCHIDOS com a condição atual
 *
 * Quem só quer uma cópia confirma sem mexer em nada, e recebe exatamente o que
 * o botão "Duplicar" dava antes. A escolha foi acrescentada, não imposta.
 *
 * ## Por que o nível del credere aparece, e é obrigatório
 *
 * No del credere o preço-base **é** a coluna escolhida. O sistema não arbitra
 * uma: seria decidir a margem do representante por conta própria, e o vendedor
 * descobriria no total — ou o cliente dele, no PDF. É a mesma regra que a
 * criação de orçamento aplica desde o Épico 9.
 *
 * ## O aviso dos itens órfãos
 *
 * Nem todo produto existe em todas as tabelas: a `123076` tem preço em CPF e no
 * del credere, e a fábrica não a vende em RT10. Duplicar para lá deixa o item
 * sem preço — e em vez de descobrir isso ao tentar emitir, o vendedor lê aqui,
 * com as referências, antes de abrir o rascunho.
 *
 * O rascunho **é criado** mesmo assim, de propósito: quem decide o que fazer com
 * o item órfão (trocar a referência, removê-lo, desistir da tabela) é ele.
 * Recusar a duplicação inteira por causa de uma linha o obrigaria a refazer as
 * outras vinte à mão.
 */

interface Props {
  orcamento: Orcamento;
  /** Duplica de fato — devolve o id do rascunho e o que ficou sem preço. */
  aoConfirmar: (condicao: {
    tabela: string;
    paymentType: string;
    delNivelPadrao?: string | null;
  }) => Promise<{ id: string; semPreco: ItemSemPreco[] }>;
  /** Abre o rascunho recém-criado. */
  aoAbrir: (id: string, tabela: string) => void;
  aoFechar: () => void;
  salvando: boolean;
  /**
   * A tabela que já vem marcada — S12. Ausente, é a do orçamento (duplicar
   * mantém a origem). O "Confira a tabela" abre o diálogo já em RT 5 / RT 10.
   */
  tabelaInicial?: string | undefined;
}

export function DuplicarComo({
  orcamento,
  aoConfirmar,
  aoAbrir,
  aoFechar,
  salvando,
  tabelaInicial,
}: Props): JSX.Element {
  const [tabela, setTabela] = useState(tabelaInicial ?? orcamento.tabela);
  const [pagamento, setPagamento] = useState(orcamento.paymentType);
  const [nivel, setNivel] = useState(orcamento.delNivelPadrao ?? '');
  const [niveis, setNiveis] = useState<NivelDelCredere[]>([]);
  const [erro, setErro] = useState<string | undefined>();
  const idExplicacao = useId();
  /** Preenchido quando a duplicação terminou com itens sem preço. */
  const [resultado, setResultado] = useState<
    { id: string; semPreco: ItemSemPreco[] } | undefined
  >();

  const paraDelCredere = tabela === TABELA_DEL_CREDERE;

  /*
   * Os seis níveis vêm do SERVIDOR — escrevê-los aqui duplicaria o pareamento
   * (coluna × comissão × del credere), e a segunda lista divergiria em silêncio:
   * o sintoma seria o preço certo com a etiqueta errada.
   *
   * Buscados só quando o destino é del credere: nos outros quadrantes o campo
   * nem existe, e uma requisição para preencher um seletor invisível é
   * desperdício.
   */
  useEffect(() => {
    if (!paraDelCredere || niveis.length > 0) return;
    void buscarNiveisDelCredere()
      .then(setNiveis)
      .catch(() => {
        setErro('Não consegui carregar as colunas do del credere.');
      });
  }, [paraDelCredere, niveis.length]);

  const mudouCondicao = tabela !== orcamento.tabela || pagamento !== orcamento.paymentType;

  async function confirmar(): Promise<void> {
    setErro(undefined);
    if (paraDelCredere && nivel === '') {
      setErro('Escolha a coluna do del credere — ela define o preço-base.');
      return;
    }
    try {
      const r = await aoConfirmar({
        tabela,
        paymentType: pagamento,
        // Fora do del credere o nível não existe: mandar `null` é explícito,
        // e a API recusa nível em CPF/RT10.
        delNivelPadrao: paraDelCredere ? nivel : null,
      });
      /*
       * Com tudo precificado, abre direto — o diálogo cumpriu o papel. Com
       * itens órfãos, ele fica: o aviso é a razão de ele existir.
       */
      if (r.semPreco.length === 0) {
        aoAbrir(r.id, tabela);
        return;
      }
      setResultado(r);
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  if (resultado !== undefined) {
    return (
      <div className="dialogo" role="dialog" aria-label="Itens sem preço na tabela escolhida">
        <div className="dialogo__caixa">
          <h2 className="dialogo__titulo">O rascunho foi criado, com uma ressalva</h2>
          <p className="dialogo__texto">
            <strong>{resultado.semPreco.length}</strong>{' '}
            {resultado.semPreco.length === 1 ? 'item não tem' : 'itens não têm'} preço em{' '}
            <strong>{rotuloTabela(tabela)}</strong> — a fábrica não vende{' '}
            {resultado.semPreco.length === 1 ? 'essa referência' : 'essas referências'} nessa
            tabela. {resultado.semPreco.length === 1 ? 'Ele fica' : 'Eles ficam'} fora dos totais, e
            a emissão só é liberada depois que você {resultado.semPreco.length === 1 ? 'o' : 'os'}{' '}
            remover ou trocar.
          </p>
          <ul className="dialogo__refs">
            {resultado.semPreco.map((i) => (
              <li key={i.itemId}>{i.referencia}</li>
            ))}
          </ul>
          <div className="dialogo__acoes">
            <button
              type="button"
              className="botao botao--primario"
              onClick={() => {
                aoAbrir(resultado.id, tabela);
              }}
            >
              Abrir o rascunho
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dialogo" role="dialog" aria-label="Duplicar como">
      <div className="dialogo__caixa">
        <h2 className="dialogo__titulo">Duplicar como…</h2>
        <p className="dialogo__texto">
          O novo rascunho nasce com os mesmos itens e quantidades, e os preços são{' '}
          <strong>recalculados</strong> na condição escolhida. O orçamento atual não muda.
        </p>

        <label className="campo">
          <span className="campo__rotulo">Tabela</span>
          <select
            className="campo__entrada"
            value={tabela}
            disabled={salvando}
            aria-describedby={idExplicacao}
            onChange={(e) => {
              setTabela(e.target.value);
              setErro(undefined);
            }}
          >
            <option value="RT10">{rotuloTabela('RT10')}</option>
            <option value="CPF">{rotuloTabela('CPF')}</option>
            <option value={TABELA_DEL_CREDERE}>{rotuloTabela(TABELA_DEL_CREDERE)}</option>
          </select>
        </label>
        <ExplicacaoTabelas id={idExplicacao} tabelas={['RT10', 'CPF', TABELA_DEL_CREDERE]} />

        {paraDelCredere && (
          <label className="campo">
            <span className="campo__rotulo">Coluna (del credere)</span>
            <select
              className="campo__entrada"
              value={nivel}
              disabled={salvando || niveis.length === 0}
              onChange={(e) => {
                setNivel(e.target.value);
                setErro(undefined);
              }}
            >
              <option value="">Selecione…</option>
              {niveis.map((n) => (
                <option key={n.nivel} value={n.nivel}>
                  {n.rotulo}
                </option>
              ))}
            </select>
            {/* A coluna É o preço-base: o sistema não escolhe uma por você. */}
            <span className="campo__dica">Ela define o preço-base de cada item.</span>
          </label>
        )}

        <label className="campo">
          <span className="campo__rotulo">Forma de pagamento</span>
          <select
            className="campo__entrada"
            value={pagamento}
            disabled={salvando}
            onChange={(e) => {
              setPagamento(e.target.value);
              setErro(undefined);
            }}
          >
            <option value="avista">{ROTULO_PAGAMENTO['avista']}</option>
            <option value="cartao">{ROTULO_PAGAMENTO['cartao']}</option>
          </select>
        </label>

        {mudouCondicao && (
          <p className="nota nota--atencao" role="status">
            A condição mudou: o novo rascunho usa o <strong>RT e o frete padrão</strong> da tabela
            escolhida, e os preços digitados à mão nos itens voltam ao preço de tabela. Descontos e
            quantidades acompanham.
          </p>
        )}

        {erro !== undefined && (
          <p className="aviso" role="alert">
            {erro}
          </p>
        )}

        <div className="dialogo__acoes">
          <button
            type="button"
            className="botao botao--neutro"
            disabled={salvando}
            onClick={aoFechar}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="botao botao--primario"
            disabled={salvando}
            onClick={() => {
              void confirmar();
            }}
          >
            {salvando ? 'Duplicando…' : 'Duplicar'}
          </button>
        </div>
      </div>
    </div>
  );
}
